/**
 * 커스텀 아이콘 공유 스토어 — 아이콘 관리에서 추가/삭제한 아이콘을 메뉴 편집 등
 * "아이콘 목록을 쓰는 모든 화면"에서 즉시 공유한다(useSyncExternalStore 외부 스토어).
 * 변경(추가/삭제) 후 refreshCustomIcons() 호출 → 모든 구독 컴포넌트가 최신 목록으로 갱신.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { listIcons, type IconRow } from '../../../api/icons';

let icons: IconRow[] = [];
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() { for (const l of listeners) l(); }
function subscribe(cb: () => void) { listeners.add(cb); return () => { listeners.delete(cb); }; }
function getSnapshot() { return icons; }

/** 서버에서 다시 읽어 모든 구독자에 반영. 추가/삭제 직후 호출. */
export async function refreshCustomIcons(): Promise<void> {
  inflight = (async () => {
    try { icons = await listIcons(); loaded = true; emit(); }
    catch { /* 무시 — 기존 목록 유지 */ }
    finally { inflight = null; }
  })();
  return inflight;
}

/** 커스텀 아이콘 목록 + 파생 맵(custom:{name}→svg) 구독. 최초 1회 자동 로드. */
export function useCustomIcons(): {
  icons: IconRow[];
  customSvg: Record<string, string>;
  customNames: string[];
} {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => { if (!loaded && !inflight) void refreshCustomIcons(); }, []);
  return useMemo(() => {
    const customSvg: Record<string, string> = {};
    const customNames: string[] = [];
    for (const ic of list) { customSvg[`custom:${ic.name}`] = ic.svg; customNames.push(`custom:${ic.name}`); }
    return { icons: list, customSvg, customNames };
  }, [list]);
}
